import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Chip,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Avatar,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toast } from "react-hot-toast";

const BankAccountDialog = ({
  open,
  onClose,
  businessLocationData,
  defaultLocationObj,
  locationId,
  countryData,
  currencyData,
  walletCurrency,
  editingAccount,
  selectedLocation,
}) => {
  const queryClient = useQueryClient();

  const [bankAccountForm, setBankAccountForm] = useState({
    locationId: locationId || "",
    country: null,
    currency: "",
    accountHolderName: "",
    bankAddress: "",
    accountNumber: "",
    iban: "",
    swift: "",
    routingNumber: "",
    bankName: "",
    bankCode: "",
    branchCode: "",
    ifsc: "",
    bsb: "",
    attachments: [],
    transitNumber: "",
    institutionNumber: "",
  });
  const [bankAccountErrors, setBankAccountErrors] = useState({});
  const [countryFieldsConfig, setCountryFieldsConfig] = useState(null);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Helper function to get country code from lat/long using reverse geocode API
  const getCountryCodeFromCoordinates = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      const data = await response.json();
      return data?.countryCode || null;
    } catch (error) {
      console.error("Error getting country code from coordinates:", error);
      return null;
    }
  };

  // Set location and default currency from parent when dialog opens, or populate form when editing
  useEffect(() => {
    if (open && locationId) {
      console.log('dialog opened for locationId:', locationId);
      setImagePreviews([]);

      if (editingAccount) {
        // Populate form with editing account data
        const list = currencyData?.data || currencyData?.currencies || [];
        const accountCountry = list.find(
          (country) => country.iso2?.toUpperCase() === editingAccount.country?.toUpperCase()
        ) || list.find(
          (country) => country.currency?.toUpperCase() === editingAccount.currency?.toUpperCase()
        );

        const urls = editingAccount.attachments.map(
          (img) => `${process.env.REACT_APP_MEDIA_URL}/uploads/images/${img}`
        );
        const imgpaths = editingAccount.attachments.map(
          (img) => `${img}`
        );

        setImages(imgpaths);
        setImagePreviews(urls);

        setBankAccountForm({
          locationId: locationId,
          country: accountCountry || null,
          currency: editingAccount.currency || "",
          accountHolderName: editingAccount.accountHolderName || "",
          bankAddress: editingAccount.bankAddress || "",
          accountNumber: editingAccount.accountNumber?.replace(/\*/g, "") || "",
          iban: editingAccount.iban || "",
          swift: editingAccount.swift || "",
          routingNumber: editingAccount.routingNumber || "",
          bankName: editingAccount.bankName || "",
          bankCode: editingAccount.bankCode || "",
          branchCode: editingAccount.branchCode || "",
          ifsc: editingAccount.ifsc || "",
          bsb: editingAccount.bsb || "",
          attachments: editingAccount.attachments || [],
          transitNumber: editingAccount.transitNumber || "",
          institutionNumber: editingAccount.institutionNumber || "",
        });
      } else {
        // Set default country and currency based on location coordinates
        const setDefaultCountryFromCoordinates = async () => {
          let latitude = null;
          let longitude = null;
          
          // Try to get coordinates from different possible formats
          const coordinates = selectedLocation?.location?.coordinates;
          if (coordinates && Array.isArray(coordinates) && coordinates.length >= 2) {
            // GeoJSON format: [longitude, latitude]
            [longitude, latitude] = coordinates;
          } else if (selectedLocation?.latitude && selectedLocation?.longitude) {
            // Separate latitude/longitude fields
            latitude = selectedLocation.latitude;
            longitude = selectedLocation.longitude;
          } else if (selectedLocation?.location?.latitude && selectedLocation?.location?.longitude) {
            // Nested latitude/longitude fields
            latitude = selectedLocation.location.latitude;
            longitude = selectedLocation.location.longitude;
          }
          
          if (latitude && longitude) {
            try {
              // Get country code from coordinates
              const countryCode = await getCountryCodeFromCoordinates(latitude, longitude);
              
              const list = currencyData?.data || currencyData?.currencies || [];
              if (countryCode && list.length) {
                // Find country in list by ISO2 code
                const defaultCountry = list.find(
                  (country) => country.iso2?.toUpperCase() === countryCode.toUpperCase()
                );
                
                if (defaultCountry) {
                  setBankAccountForm((prev) => ({
                    ...prev,
                    locationId: locationId,
                    country: defaultCountry,
                    currency: defaultCountry.currency || walletCurrency || prev.currency || "",
                  }));
                  return;
                }
              }
            } catch (error) {
              console.error("Error setting default country from coordinates:", error);
            }
          }
          
          // Fallback: just set currency if coordinates not available
          setBankAccountForm((prev) => {
            const shouldSetDefaults = !prev.currency;
            return {
              ...prev,
              locationId: locationId,
              currency: shouldSetDefaults 
                ? (walletCurrency || prev.currency || "") 
                : prev.currency,
            };
          });

          // const urls = editingAccount.attachments.map(
          //   (img) => `${process.env.REACT_APP_MEDIA_URL}/uploads/images/${img}`
          // );
          // const imgpaths = editingAccount.attachments.map(
          //   (img) => `${img}`
          // );
          
          // setImages(imgpaths);
          // setImagePreviews(urls);
        };

        setDefaultCountryFromCoordinates();
      }
    }
  }, [open, locationId, walletCurrency, currencyData, editingAccount, selectedLocation]);

  // Get country code for API call
  const countryCode = bankAccountForm.country
    ? (bankAccountForm.country.iso2 || "").toUpperCase()
    : null;

  // Fetch country-specific fields from API
  const { data: countryFieldsData, isLoading: isLoadingFields } = useQuery(
    ["bankAccountFields", countryCode],
    async ({ signal }) => {
      if (!countryCode) return null;
      const res = await axiosInstance.get(
        ApiEndPoints.BANK_ACCOUNT.getCountryFields(countryCode),
        { signal }
      );
      return res.data.data;
    },
    {
      enabled: !!countryCode,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setCountryFieldsConfig(data);
          // Update currency from API if provided
          if (data.currency) {
            setBankAccountForm((prev) => ({
              ...prev,
              currency: data.currency,
            }));
          }
        }
      },
      onError: () => {
        setCountryFieldsConfig(null);
      },
    }
  );

  // Get currency based on country (fallback if API doesn't provide)
  useEffect(() => {
    if (bankAccountForm.country && !countryFieldsConfig?.currency) {
      // Currency data is already in the country object from currency API
      if (bankAccountForm.country.currency) {
        setBankAccountForm((prev) => ({
          ...prev,
          currency: bankAccountForm.country.currency || "",
        }));
      }
    }
  }, [bankAccountForm.country, countryFieldsConfig]);

  // Helper function to build payload
  const buildPayload = (formData, images=[], isUpdate = false) => {
    const payload = {
      accountHolderName: formData.accountHolderName?.trim() || undefined,
      accountHolderType: "business",
      bankAddress: formData.bankAddress?.trim() || undefined,
      accountNumber: formData.accountNumber?.trim() || undefined,
      iban: formData.iban?.toUpperCase().trim() || undefined,
      swift: formData.swift?.toUpperCase().trim() || undefined,
      routingNumber: formData.routingNumber?.trim() || undefined,
      bankName: formData.bankName?.trim() || undefined,
      bankCode: formData.bankCode?.trim() || undefined,
      branchCode: formData.branchCode?.trim() || undefined,
      ifsc: formData.ifsc?.toUpperCase().trim() || undefined,
      bsb: formData.bsb?.trim() || undefined,
      transitNumber: formData.transitNumber?.trim() || undefined,
      institutionNumber: formData.institutionNumber?.trim() || undefined,
    };

    // Only add these fields for create, not update
    if (!isUpdate) {
      const selectedLocationId = formData.locationId || locationId;
      payload.ownerId = selectedLocationId;
      payload.ownerType = "business_location";
      payload.country = formData.country?.name?.toUpperCase() || "";
      payload.currency = formData.currency?.toUpperCase() || undefined;
      payload.isDefault = false;
    }

    // Remove undefined and empty string values
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined || payload[key] === "") {
        delete payload[key];
      }
    });

    if (images.length) {
      const fd = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        fd.append(key, value);
      });


      images.forEach((file) => {
        if (file instanceof File) {
          fd.append("attachments", file, file.name);
        }
      });

      return fd;
    }

    return payload;
  };

  // Create bank account mutation
  const createBankAccountMutation = useMutation(
    async (formData) => {
      const selectedLocationId = formData.locationId || locationId;
      const payload = buildPayload(formData, images, false);

      const response = await axiosInstance.post(
        ApiEndPoints.BANK_ACCOUNT.create,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return { data: response.data, locationId: selectedLocationId };
    },
    {
      onSuccess: (result) => {
        toast.success("bank account linked  ");
        queryClient.invalidateQueries(["walletData", result.locationId]);
        queryClient.invalidateQueries(["bankAccounts", result.locationId]);
        onClose();
        resetBankAccountForm();
        setImages([]);
        setImagePreviews([]);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || "failed to link bank account";
        toast.error(errorMessage);
        if (error?.response?.data?.errors) {
          setBankAccountErrors(error.response.data.errors);
        }
      },
    }
  );

  // Update bank account mutation
  const updateBankAccountMutation = useMutation(
    async (formData) => {
      const payload = buildPayload(formData, images, true);

      const response = await axiosInstance.patch(
        ApiEndPoints.BANK_ACCOUNT.update(editingAccount.id),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return { data: response.data, locationId: locationId };
    },
    {
      onSuccess: (result) => {
        toast.success("bank account updated");
        queryClient.invalidateQueries(["walletData", result.locationId]);
        queryClient.invalidateQueries(["bankAccounts", result.locationId]);
        onClose();
        resetBankAccountForm();
        setImages([]);
        setImagePreviews([]);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || "failed to update bank account";
        toast.error(errorMessage);
        if (error?.response?.data?.errors) {
          setBankAccountErrors(error.response.data.errors);
        }
      },
    }
  );

  // Reset bank account form
  const resetBankAccountForm = () => {
    setBankAccountForm({
      locationId: locationId || "",
      country: null,
      currency: "",
      accountHolderName: "",
      bankAddress: "",
      accountNumber: "",
      iban: "",
      swift: "",
      routingNumber: "",
      bankName: "",
      bankCode: "",
      branchCode: "",
      ifsc: "",
      bsb: "",
      transitNumber: "",
      attachments: [],
      institutionNumber: "",
    });
    setBankAccountErrors({});
    setCountryFieldsConfig(null);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    setImages((prev) => [...prev, ...files]);

    const previews = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews).then((newPreviews) => {
      setImagePreviews((prev) => [...prev, ...newPreviews]);
    });
  };

  const handleRemoveImage = async (index) => {
    const imageName = images[index];

    try {
      await axiosInstance.delete(
        ApiEndPoints.BANK_ACCOUNT.deleteAttachment(editingAccount.id, imageName),
      );

      setImages((prev) => prev.filter((_, i) => i !== index));
      setImagePreviews((prev) => {
        URL.revokeObjectURL(prev[index]);
        return prev.filter((_, i) => i !== index);
      });
    } catch (error) {
      console.error("Failed to delete image", error);
    }
  };

  // Handle bank account form change
  const handleBankAccountFormChange = (field, value) => {
    setBankAccountForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Set currency when country is selected
      if (field === "country" && value && value.currency) {
        updated.currency = value.currency;
      }
      return updated;
    });
    if (bankAccountErrors[field]) {
      setBankAccountErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Reset country fields config when country changes
    if (field === "country") {
      setCountryFieldsConfig(null);
    }
  };

  // Handle bank account form submit
  const handleBankAccountSubmit = () => {
    const errors = {};
    const isEditing = !!editingAccount;
    
    // Use locationId from props (parent WalletSetup)
    const selectedLocationId = locationId || bankAccountForm.locationId;
    if (!isEditing && !selectedLocationId) {
      errors.locationId = "location is required";
    }
    // Country and currency are only required for new accounts, not updates
    if (!isEditing) {
      if (!bankAccountForm.country) {
        errors.country = "country is required";
      }
      if (!bankAccountForm.currency) {
        errors.currency = "currency is required";
      }
    }
    if (!bankAccountForm.accountHolderName?.trim()) {
      errors.accountHolderName = "account holder name is required";
    }

    // Validate required fields from API
    if (countryFieldsConfig?.requiredFields) {
      countryFieldsConfig.requiredFields.forEach((field) => {
        const formField = field; // Field names match directly
        if (!bankAccountForm[formField]?.trim()) {
          errors[formField] = `${getFieldLabel(field)} is required`;
        }
      });
    } else {
      // Fallback validation if API data not available
      if (!bankAccountForm.accountNumber?.trim() && !bankAccountForm.iban?.trim()) {
        errors.accountNumber = "account number or iban is required";
        errors.iban = "account number or iban is required";
      }
    }

    // Validate IFSC code format if provided
    if (bankAccountForm.ifsc?.trim()) {
      const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscPattern.test(bankAccountForm.ifsc.trim())) {
        errors.ifsc = "ifsc code must be 11 characters: 4 letters, 0, then 6 alphanumeric characters (e.g., ABCD0123456)";
      }
    }

    if (Object.keys(errors).length > 0) {
      setBankAccountErrors(errors);
      return;
    }

    // Ensure locationId from parent is used
    const formDataWithLocation = {
      ...bankAccountForm,
      locationId: locationId || bankAccountForm.locationId,
    };
    
    // Use update mutation if editing, otherwise create
    if (editingAccount) {
      updateBankAccountMutation.mutate(formDataWithLocation);
    } else {
      createBankAccountMutation.mutate(formDataWithLocation);
    }
  };

  // Helper function to check if a field should be shown
  const shouldShowField = (fieldName) => {
    if (!countryFieldsConfig) return false;
    
    const allFields = [
      ...(countryFieldsConfig.requiredFields || []),
      ...(countryFieldsConfig.optionalFields || []),
    ];
    
    return allFields.includes(fieldName);
  };

  // Helper function to check if a field is required
  const isFieldRequired = (fieldName) => {
    if (!countryFieldsConfig?.requiredFields) return false;
    return countryFieldsConfig.requiredFields.includes(fieldName);
  };

  // Map API field names to form field labels
  const getFieldLabel = (fieldName) => {
    const labelMap = {
      ifsc: "ifsc code",
      accountNumber: "bank account number",
      bankName: "bank name",
      iban: "iban",
      swift: "swift/bic code",
      routingNumber: "routing number",
      branchCode: "branch code",
      bankAddress: "bank address",
      bsb: "bsb code",
      transitNumber: "transit number",
      institutionNumber: "institution number",
      bankCode: "bank code",
    };
    return labelMap[fieldName] || fieldName;
  };

  const handleClose = () => {
    onClose();
    resetBankAccountForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: { xs: "90%", sm: "70%", md: "50%", lg: "40%" },
          maxWidth: { xs: "90%", sm: "500px", md: "450px", lg: "400px" },
          maxHeight: "90vh",
          m: { xs: 2, sm: 3 },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(45, 45, 45, 0.13)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          bgcolor: "rgba(242, 233, 255, 1)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#222",
            textTransform: "lowercase",
          }}
        >
          link bank account
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "#666" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent 
        dividers 
        sx={{ 
          bgcolor: "rgba(242, 233, 255, 1)", 
          p: { xs: 2, sm: 3 },
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#7c28d6",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Header with location and currency */}
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                textTransform: "lowercase",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              location (country) | currency: {bankAccountForm.currency || "USD"}
            </Typography>
          </Box>

          {/* Country Selection */}
          <Box>
           
            <Autocomplete
              fullWidth
        
              options={currencyData?.data || currencyData?.currencies || []}
              getOptionLabel={(option) => {
                if (!option) return "";
                const currencyCode = option.currency || "";
                const currencySymbol = option.symbol || "";
                if (currencyCode && currencySymbol) {
                  return `${option.name} (${currencyCode.toLowerCase()}) ${currencySymbol}`;
                } else if (currencyCode) {
                  return `${option.name} (${currencyCode.toLowerCase()})`;
                } else if (currencySymbol) {
                  return `${option.name} ${currencySymbol}`;
                }
                return option.name || "";
              }}
              value={bankAccountForm.country}
              onChange={(event, newValue) => {
                handleBankAccountFormChange("country", newValue);
              }}
              isOptionEqualToValue={(option, value) => option._id === value?._id}
              disabled={true}
              renderOption={(props, option) => {
                const currencyCode = option.currency || "";
                const currencySymbol = option.symbol || "";
                
                return (
                  <Box 
                    component="li" 
                    {...props} 
                    sx={{ 
                      textTransform: "lowercase",
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      px: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                      <Typography 
                        component="span" 
                        sx={{ 
                          fontSize: "0.9375rem",
                          fontWeight: 500,
                          color: "#222",
                        }}
                      >
                        {option.name}
                      </Typography>
                      {currencyCode && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.9375rem",
                            color: "#666",
                          }}
                        >
                          ({currencyCode.toLowerCase()})
                        </Typography>
                      )}
                      {currencySymbol && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 600,
                            color: "#7c28d6",
                          }}
                        >
                          {currencySymbol}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => {
                const selectedCountry = bankAccountForm.country;
                const currencyCode = selectedCountry?.currency || "";
                const currencySymbol = selectedCountry?.symbol || "";

                return (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="select your country here"
                    error={Boolean(bankAccountErrors.country)}
                    helperText={bankAccountErrors.country}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        color: "#fff",
                      },
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: {
                        color: "#fff",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        bgcolor: "#7c28d6",
                        color: "#fff",
                        "& fieldset": {
                          borderColor: "#7c28d6",
                        },
                        "&:hover fieldset": {
                          borderColor: "#6a1fb8",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#7c28d6",
                        },
                        "&.Mui-disabled": {
                          bgcolor: "#7c28d6",
                          color: "#fff",
                          "& fieldset": {
                            borderColor: "#7c28d6",
                          },
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                        textTransform: "lowercase",
                        "&.Mui-disabled": {
                          color: "#fff !important",
                          WebkitTextFillColor: "#fff !important",
                        },
                      },
                      "& .MuiAutocomplete-input": {
                        color: "#fff !important",
                        "&.Mui-disabled": {
                          color: "#fff !important",
                          WebkitTextFillColor: "#fff !important",
                        },
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(247, 241, 241, 0.7)",
                        opacity: 1,
                      },
                    }}
                  />
                );
              }}
              noOptionsText="no countries found"
              filterOptions={(options, { inputValue }) =>
                options.filter((option) =>
                  option.name?.toLowerCase().includes(inputValue.toLowerCase())
                )
              }
            />
          </Box>

          {/* Loading indicator while fetching country fields */}
          {isLoadingFields && bankAccountForm.country && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} sx={{ color: "#7c28d6" }} />
            </Box>
          )}

          {/* Dynamic fields based on API response */}
          {countryFieldsConfig && (
            <>
              {/* Bank Name */}
              {shouldShowField("bankName") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("bankName")}
                  value={bankAccountForm.bankName}
                  onChange={(e) => handleBankAccountFormChange("bankName", e.target.value)}
                  error={Boolean(bankAccountErrors.bankName)}
                  helperText={bankAccountErrors.bankName}
                  required={isFieldRequired("bankName")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      textTransform: "lowercase",
                    },
                  }}
                />
              )}

              {/* Bank Address */}
              {shouldShowField("bankAddress") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("bankAddress")}
                  value={bankAccountForm.bankAddress}
                  onChange={(e) => handleBankAccountFormChange("bankAddress", e.target.value)}
                  error={Boolean(bankAccountErrors.bankAddress)}
                  helperText={bankAccountErrors.bankAddress}
                  required={isFieldRequired("bankAddress")}
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      textTransform: "lowercase",
                    },
                  }}
                />
              )}

              {/* IFSC Code */}
              {shouldShowField("ifsc") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("ifsc")}
                  value={bankAccountForm.ifsc}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                    // Limit to 11 characters: 4 letters, 0, 6 alphanumeric
                    const limitedValue = value.slice(0, 11);
                    handleBankAccountFormChange("ifsc", limitedValue);
                  }}
                  error={Boolean(bankAccountErrors.ifsc)}
                  helperText={bankAccountErrors.ifsc || "format: 4 letters, 0, then 6 alphanumeric (e.g., ABCD0123456)"}
                  required={isFieldRequired("ifsc")}
                  inputProps={{
                    maxLength: 11,
                    pattern: "^[A-Z]{4}0[A-Z0-9]{6}$",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      textTransform: "uppercase",
                    },
                  }}
                />
              )}

              {/* IBAN */}
              {shouldShowField("iban") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("iban")}
                  value={bankAccountForm.iban}
                  onChange={(e) => handleBankAccountFormChange("iban", e.target.value.toUpperCase())}
                  error={Boolean(bankAccountErrors.iban)}
                  helperText={bankAccountErrors.iban}
                  required={isFieldRequired("iban")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      textTransform: "uppercase",
                    },
                  }}
                />
              )}

              {/* SWIFT/BIC Code */}
              {shouldShowField("swift") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("swift")}
                  value={bankAccountForm.swift}
                  onChange={(e) => handleBankAccountFormChange("swift", e.target.value.toUpperCase())}
                  error={Boolean(bankAccountErrors.swift)}
                  helperText={bankAccountErrors.swift}
                  required={isFieldRequired("swift")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      textTransform: "uppercase",
                    },
                  }}
                />
              )}

              {/* Routing Number */}
              {shouldShowField("routingNumber") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("routingNumber")}
                  value={bankAccountForm.routingNumber}
                  onChange={(e) => handleBankAccountFormChange("routingNumber", e.target.value)}
                  error={Boolean(bankAccountErrors.routingNumber)}
                  helperText={bankAccountErrors.routingNumber}
                  required={isFieldRequired("routingNumber")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    },
                  }}
                />
              )}

              {/* Bank Account Number */}
              {shouldShowField("accountNumber") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("accountNumber")}
                  value={bankAccountForm.accountNumber}
                  onChange={(e) => handleBankAccountFormChange("accountNumber", e.target.value)}
                  error={Boolean(bankAccountErrors.accountNumber)}
                  helperText={bankAccountErrors.accountNumber}
                  required={isFieldRequired("accountNumber")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    },
                  }}
                />
              )}

              {/* Branch Code */}
              {shouldShowField("branchCode") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("branchCode")}
                  value={bankAccountForm.branchCode}
                  onChange={(e) => handleBankAccountFormChange("branchCode", e.target.value)}
                  error={Boolean(bankAccountErrors.branchCode)}
                  helperText={bankAccountErrors.branchCode}
                  required={isFieldRequired("branchCode")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    },
                  }}
                />
              )}

              {/* Bank Code (for non-IFSC countries like Japan, New Zealand, Indonesia) */}
              {shouldShowField("bankCode") && !shouldShowField("ifsc") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("bankCode")}
                  value={bankAccountForm.bankCode}
                  onChange={(e) => handleBankAccountFormChange("bankCode", e.target.value)}
                  error={Boolean(bankAccountErrors.bankCode)}
                  helperText={bankAccountErrors.bankCode}
                  required={isFieldRequired("bankCode")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    },
                  }}
                />
              )}

              {/* BSB Code (Australia) */}
              {shouldShowField("bsb") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("bsb")}
                  value={bankAccountForm.bsb}
                  onChange={(e) => handleBankAccountFormChange("bsb", e.target.value)}
                  error={Boolean(bankAccountErrors.bsb)}
                  helperText={bankAccountErrors.bsb}
                  required={isFieldRequired("bsb")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    },
                  }}
                />
              )}

              {/* Transit Number (Canada) */}
              {shouldShowField("transitNumber") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("transitNumber")}
                  value={bankAccountForm.transitNumber}
                  onChange={(e) => handleBankAccountFormChange("transitNumber", e.target.value)}
                  error={Boolean(bankAccountErrors.transitNumber)}
                  helperText={bankAccountErrors.transitNumber}
                  required={isFieldRequired("transitNumber")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    },
                  }}
                />
              )}

              {/* Institution Number (Canada) */}
              {shouldShowField("institutionNumber") && (
                <TextField
                  fullWidth
                  label={getFieldLabel("institutionNumber")}
                  value={bankAccountForm.institutionNumber}
                  onChange={(e) => handleBankAccountFormChange("institutionNumber", e.target.value)}
                  error={Boolean(bankAccountErrors.institutionNumber)}
                  helperText={bankAccountErrors.institutionNumber}
                  required={isFieldRequired("institutionNumber")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    },
                  }}
                />
              )}
            </>
          )}

          {/* Fallback fields when API data is not available */}
          {!countryFieldsConfig && bankAccountForm.country && (
            <>
              <TextField
                fullWidth
                label="bank name"
                value={bankAccountForm.bankName}
                onChange={(e) => handleBankAccountFormChange("bankName", e.target.value)}
                error={Boolean(bankAccountErrors.bankName)}
                helperText={bankAccountErrors.bankName}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    bgcolor: "#fff",
                    textTransform: "lowercase",
                  },
                }}
              />
              <TextField
                fullWidth
                label="bank address"
                value={bankAccountForm.bankAddress}
                onChange={(e) => handleBankAccountFormChange("bankAddress", e.target.value)}
                error={Boolean(bankAccountErrors.bankAddress)}
                helperText={bankAccountErrors.bankAddress}
                multiline
                rows={2}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    bgcolor: "#fff",
                    textTransform: "lowercase",
                  },
                }}
              />
              <TextField
                fullWidth
                label="bank account number"
                value={bankAccountForm.accountNumber}
                onChange={(e) => handleBankAccountFormChange("accountNumber", e.target.value)}
                error={Boolean(bankAccountErrors.accountNumber)}
                helperText={bankAccountErrors.accountNumber}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    bgcolor: "#fff",
                  },
                }}
              />
            </>
          )}

          {/* Account Holder Name */}
          <TextField
            fullWidth
            label="account holder name"
            value={bankAccountForm.accountHolderName}
            onChange={(e) => handleBankAccountFormChange("accountHolderName", e.target.value)}
            error={Boolean(bankAccountErrors.accountHolderName)}
            helperText={bankAccountErrors.accountHolderName}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                bgcolor: "#fff",
                textTransform: "lowercase",
              },
            }}
          />
        </Box>

        {/* Upload section */}
        <Box
          sx={{
            border: "2px dashed #BDBDBD",
            borderRadius: 3,
            minHeight: 100,
            display: "flex",
            flexDirection: imagePreviews.length ?  "row" :"column",
            alignItems: "center",
            position: "relative",
            bgcolor: "#faf6ff",
            marginTop: 2,
            padding: 2,
            mb: 0.5,
            justifyContent: imagePreviews.length ? "flex-start" : "center",
            transition: "border .2s",
            "&:hover": { borderColor: "#8000c2" },
            cursor: "pointer",
            width: "100%",
            gap:2,
            boxSizing: "border-box",
            cursor: "pointer",
          }}
          component="label"
          htmlFor="attachments"
        >
          {imagePreviews.length ? (
            imagePreviews.map((src, index) => (
              <Box key={index} sx={{ position: "relative" }}>
                <Avatar
                  key={index}
                  src={src}
                  variant="rounded"
                  sx={{
                    width: 90,
                    height: 90,
                    gap: 5,
                    borderRadius: 2,
                    objectFit: "cover",
                  }}
                />

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(index);
                  }}
                  sx={{
                    position: "absolute",
                    display: "none", // make it hidden for future use
                    top: -8,
                    right: -8,
                    bgcolor: "white",
                    boxShadow: 1,
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: "center", py: 1.2 }}>
              <CloudUploadOutlinedIcon
                sx={{ color: "#ae61e3", fontSize: 30, mb: 0.5 }}
              />
              <Typography
                sx={{
                  color: "#8000c2",
                  fontSize: 16,
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                upload bank statement or bank confirmation letter
              </Typography>
            </Box>
          )}
          <input
            id="attachments"
            type="file"
            accept="image/*"
            multiple={true}
            hidden
            onChange={handleImageChange}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, sm: 3 }, bgcolor: "rgba(242, 233, 255, 1)" }}>
    
        <Button
          onClick={handleBankAccountSubmit}
          variant="contained"
          disabled={createBankAccountMutation.isLoading || updateBankAccountMutation.isLoading}
          sx={{
            bgcolor: "#7c28d6",
            color: "#fff",
            textTransform: "lowercase",
            px: 3,
            "&:hover": {
              bgcolor: "#6a1fb8",
            },
            "&:disabled": {
              bgcolor: "#ccc",
            },
          }}
        >
          {editingAccount 
            ? (updateBankAccountMutation.isLoading ? "updating..." : "update")
            : (createBankAccountMutation.isLoading ? "linking..." : "link")
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankAccountDialog;

